import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { BreadCrumb, TableContainer } from "@/components";
import { StatusOptions, SubscriptionOptions } from "@/constants";
import { getUsers } from "@/features";
import { OptionProps, UserType } from "@/types";
import { downloadExcelFile } from "@/utils";
import { ColumnDef } from "@tanstack/react-table";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import { CreateOrEditUser } from "./components/CreateOrEditUser";
import { DeleteUser } from "./components/DeleteUser";
import { EnableOrDisableUser } from "./components/EnableOrDisableUser";
import { FilterProps } from "./types";

const Colors: Record<string, string> = {
  user: "primary",
  agency_client: "success",
  agency_admin: "info",
};

const UserManagement = () => {
  const { userList } = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  const [data, setData] = useState<UserType[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [isDisable, setIsDisable] = useState(false);
  const [filter, setFilter] = useState<FilterProps>({
    combineKey: "",
    subscription: null,
    status: null,
    fromDate: "",
    toDate: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const columns = useMemo<ColumnDef<UserType>[]>(
    () => [
      {
        header: "Email",
        accessorKey: "email",
        disableFilters: true,
        enableColumnFilter: false,
      },
      {
        header: "Subscription Name",
        accessorKey: "subscription.package_name",
        disableFilters: true,
        enableColumnFilter: false,
      },
      {
        header: "Confirmed Date",
        accessorKey: "subscription.created_at",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) =>
          moment(row.original.subscription?.created_at).format("DD/MM/YYYY"),
      },
      {
        header: "User Type",
        accessorKey: "user_type",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) => {
          return (
            <div className="text-capitalize">
              <Badge
                color={Colors?.[row.original.user_type]}
                style={{ fontSize: 13 }}
              >
                {row.original.user_type}
              </Badge>
            </div>
          );
        },
      },
      {
        header: "Expiry Date",
        accessorKey: "subscription.expose_expire_date",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) => {
          return row.original.subscription?.expose_expire_date
            ? moment(row.original.subscription?.expose_expire_date).format(
                "DD/MM/YYYY",
              )
            : "";
        },
      },
      {
        header: "Status",
        accessorKey: "subscription.status_name",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) => {
          return (
            <Badge
              color={
                row.original.subscription?.status_name?.toLowerCase() ===
                "active"
                  ? "success"
                  : "danger"
              }
              style={{ fontSize: 13 }}
            >
              {row.original?.subscription?.status_name}
            </Badge>
          );
        },
      },
      {
        header: "Action",
        disableFilters: true,
        enableColumnFilter: false,
        cell: ({ row }) => {
          return (
            <div>
              <Button
                color="primary"
                className="btn-sm"
                onClick={() => {
                  setSelectedId(row.original.id as number);
                  setIsVisible(true);
                  if (row.original.is_active) {
                    setIsDisable(true);
                  } else {
                    setIsDisable(false);
                  }
                }}
              >
                {row.original.is_active ? "Disable" : "Enable"}
              </Button>
              <Button
                color="primary"
                className="btn-sm ms-2"
                style={{ minWidth: 58 }}
                onClick={() => {
                  setSelectedId(row.original.id as number);
                  setIsOpen(true);
                }}
              >
                Edit
              </Button>
              <Button
                color="danger"
                className="btn-sm ms-2"
                style={{ minWidth: 58 }}
                onClick={() => {
                  setSelectedId(row.original.id as number);
                  setIsDelete(true);
                }}
              >
                Delete
              </Button>
              <Button
                color="primary"
                className="btn-sm ms-2"
                style={{ minWidth: 58 }}
                onClick={() =>
                  window.open(
                    `${process.env.REACT_APP_REDIRECT_URL}?token=${row.original.sign_in_token}&type=mock`,
                    "_blank",
                  )
                }
              >
                Sign In As User
              </Button>
            </div>
          );
        },
      },
    ],
    [],
  );

  const getData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await dispatch(getUsers()).unwrap();
      setData(response);
    } catch (error) {
      console.error("error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  const onApply = () => {
    let filteredData = userList;

    if (filter.combineKey) {
      filteredData = filteredData.filter((item: UserType) =>
        item.email?.toLowerCase().includes(filter.combineKey?.toLowerCase()),
      );
    }

    if (filter.subscription) {
      filteredData = filteredData.filter(
        (item: UserType) =>
          item.subscription?.package_name?.toLowerCase() ===
          filter.subscription?.value?.toLowerCase(),
      );
    }

    if (filter.status) {
      filteredData = filteredData.filter((item: UserType) =>
        filter.status?.value?.toLowerCase() === "inactive"
          ? item.subscription?.status_name?.toLowerCase() === "terminated"
          : item.subscription?.status_name?.toLowerCase() ===
            filter.status?.value,
      );
    }

    if (filter.fromDate) {
      filteredData = filteredData.filter((data: UserType) =>
        moment(filter.fromDate).isSameOrBefore(
          moment(data.subscription?.created_at),
        ),
      );
    }

    if (filter.toDate) {
      filteredData = filteredData.filter((data: UserType) => {
        const expireDate = data.subscription?.expose_expire_date;
        if (!expireDate) return false;

        return moment(filter.toDate, "DD/MM/YYYY").isSameOrAfter(
          moment(expireDate),
        );
      });
    }

    setData(filteredData);
  };

  const onReset = () => {
    setFilter({
      combineKey: "",
      subscription: null,
      status: null,
      fromDate: "",
      toDate: "",
    });
    setData(userList);
  };

  const onExportCSV = () => {
    const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const fileName = `User_Management_${currentDate}.csv`;

    const formattedData = data.map((item) => ({
      email: item.email,
      subscriptionName: item.subscription?.package_name,
      confirmedDate: `="${moment(item.subscription?.created_at).format(
        "DD/MM/YYYY",
      )}"`,
      userType: item.user_type,
      expiryDate: `="${moment(item.subscription?.expose_expire_date).format(
        "DD/MM/YYYY",
      )}"`,
      status: item.subscription?.status_name,
    }));

    downloadExcelFile(formattedData, fileName);
  };

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="User Management" />
          <Card>
            <CardBody>
              <div className="mb-3 text-end">
                <Button color="primary" onClick={() => setIsOpen(true)}>
                  <i className="ri-add-line me-2" />
                  Create New User
                </Button>
              </div>
              <Row>
                <Col xs={12} lg={4}>
                  <FormGroup>
                    <Label>Search</Label>
                    <div className="form-icon right">
                      <Input
                        placeholder="Search by email"
                        value={filter.combineKey}
                        onChange={(e) =>
                          setFilter({
                            ...filter,
                            combineKey: e.target.value,
                          })
                        }
                      />
                      <i className="ri-search-line" />
                    </div>
                  </FormGroup>
                </Col>
                <Col xs={12} lg={4}>
                  <FormGroup>
                    <Label>Subscription</Label>
                    <Select
                      options={SubscriptionOptions}
                      value={filter.subscription}
                      placeholder="Select subscription"
                      onChange={(item: OptionProps) => {
                        setFilter({
                          ...filter,
                          subscription: item,
                        });
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col xs={12} lg={4}>
                  <FormGroup>
                    <Label>Status</Label>
                    <Select
                      options={StatusOptions}
                      value={filter.status}
                      placeholder="Select status"
                      onChange={(item: OptionProps) => {
                        setFilter({
                          ...filter,
                          status: item,
                        });
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col xs={12} lg={4}>
                  <FormGroup>
                    <Label>From Date</Label>
                    <Flatpickr
                      className="form-control"
                      options={{
                        dateFormat: "d/m/Y",
                      }}
                      placeholder="Users was created from"
                      value={filter.fromDate}
                      onChange={(_, date) => {
                        console.log("from date", date);
                        setFilter({
                          ...filter,
                          fromDate: date,
                        });
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col xs={12} lg={4}>
                  <FormGroup>
                    <Label>To Date</Label>
                    <Flatpickr
                      className="form-control"
                      options={{
                        dateFormat: "d/m/Y",
                      }}
                      placeholder="Users was created to"
                      value={filter.toDate}
                      onChange={(_, date) => {
                        console.log("to date", date);
                        setFilter({
                          ...filter,
                          toDate: date,
                        });
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col xs={12} lg={3} className="mt-auto">
                  <FormGroup>
                    <Button color="primary" onClick={onApply}>
                      Apply
                    </Button>
                    <Button color="light" className="ms-2" onClick={onReset}>
                      Reset
                    </Button>
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <h4 className="card-title mb-0 flex-grow-1">User List</h4>
              <FormGroup className="custom-form-group" onClick={onExportCSV}>
                <Button color="primary">
                  <i className="ri-file-excel-2-line me-2" />
                  Export CSV
                </Button>
              </FormGroup>
            </CardHeader>
            <CardBody>
              <TableContainer
                columns={columns || []}
                data={data || []}
                customPageSize={10}
                isLoading={isLoading}
                tableClass="table-centered align-middle table-nowrap mb-0"
                theadClass="text-muted table-light"
                divClass="table-responsive"
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      {isOpen && (
        <CreateOrEditUser
          isOpen={isOpen}
          selectedId={selectedId as number}
          onSuccess={() => getData()}
          toggle={() => {
            setIsOpen(false);
            setSelectedId(null);
          }}
        />
      )}

      {isVisible && (
        <EnableOrDisableUser
          isOpen={isVisible}
          isDisable={isDisable}
          selectedId={selectedId as number}
          onSuccess={() => getData()}
          toggle={() => {
            setIsVisible(false);
            setSelectedId(null);
          }}
        />
      )}

      {isDelete && (
        <DeleteUser
          isOpen={isDelete}
          selectedId={selectedId as number}
          onSuccess={() => getData()}
          toggle={() => {
            setIsDelete(false);
            setSelectedId(null);
          }}
        />
      )}
    </>
  );
};

export default UserManagement;
